import {
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { InstrumentTypeResponse } from "../../codegen-api";
import { SPACING } from "../../constants/design/spacing";
import { ComponentTitle } from "../PortfolioSettings/style";
import { PortfolioMarketFilter } from "../PortfolioSettings/PortfolioMarketFilter";
import { Header } from "../PortfolioSettings/Table/style";
import { TableContainerWrapper } from "../shared/Table/style";
import { HistoryTabsHeaderActions, TradingScrollArea, TradingShell } from "./style";

interface IHistorySectionLayoutProps {
  /** Section title (shown above the filter dropdown). */
  title: string;
  /** Selected instrument-type filter; undefined = "all". */
  filter?: InstrumentTypeResponse;
  /** Filter change handler. When omitted, the filter dropdown is hidden. */
  onSetFilter?: (filter?: InstrumentTypeResponse) => void;
  /** Hides the section title — useful when embedded in a bottom bar. */
  hideTitle?: boolean;
  /**
   * "portfolio" (default) — page-level scroll, table sized via `calc()`.
   * "trading"             — embedded in a bottom-bar drawer with its own
   *                         scroll surface; layout becomes a flex column
   *                         that fills the drawer height.
   */
  mode?: "portfolio" | "trading";
  /**
   * Render-prop body.
   * - In `portfolio` mode it receives the measured header height (number)
   *   so the body can size with `calc(100% - height)`.
   * - In `trading` mode it receives `undefined` — meaning "size to content";
   *   `<TradingScrollArea>` owns the vertical scroll, so the body must NOT
   *   set its own height (which would re-introduce internal scrolling and
   *   block the outer scroll surface).
   */
  children: (tableHeight: number | undefined) => ReactNode;
}

/**
 * Header + filter + table chrome shared by Position / Trade / Order
 * history pages.
 *
 * In "portfolio" mode it lays out as a vanilla `TableContainerWrapper`,
 * passing the measured header height down so the body can subtract it.
 *
 * In "trading" mode (embedded in the bottom-bar drawer) the layout
 * switches to a flex column with its own scroll surface — the bottom
 * bar's outer `Container` is `overflow: scroll` and its `ContentContainer`
 * is `overflow: hidden`, so without an inner scrollable region the rows
 * get clipped instead of scrolled. `<TradingScrollArea>` provides the
 * `overflow-y: auto` region that `IntersectionObserver` will use as
 * its root.
 */
function HistorySectionLayout({
  title,
  filter,
  onSetFilter,
  hideTitle,
  mode = "portfolio",
  children,
}: IHistorySectionLayoutProps) {
  const headerRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);

  useLayoutEffect(() => {
    setTableHeight(
      Number(headerRef.current?.clientHeight || 0) + SPACING.five
    );
  }, [title]);

  const isTrading = mode === "trading";

  if (isTrading) {
    return (
      <TradingShell>
        <Header ref={headerRef} isMobile>
          {!hideTitle && <ComponentTitle>{title}</ComponentTitle>}
          {onSetFilter && (
            <HistoryTabsHeaderActions>
              <PortfolioMarketFilter
                title={title}
                instrumentType={filter}
                onSetFilter={onSetFilter}
              />
            </HistoryTabsHeaderActions>
          )}
        </Header>
        <TradingScrollArea>{children(undefined)}</TradingScrollArea>
      </TradingShell>
    );
  }

  return (
    <TableContainerWrapper $isMobile={false}>
      <Header ref={headerRef}>
        {!hideTitle && <ComponentTitle>{title}</ComponentTitle>}
        {onSetFilter && (
          <HistoryTabsHeaderActions>
            <PortfolioMarketFilter
              title={title}
              instrumentType={filter}
              onSetFilter={onSetFilter}
            />
          </HistoryTabsHeaderActions>
        )}
      </Header>
      {children(tableHeight)}
    </TableContainerWrapper>
  );
}

export default HistorySectionLayout;
